import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../lib/api';
import { useLiveEvents } from '../context/LiveEventsContext';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, CalendarDays, Megaphone, FileText, DollarSign, Heart, MessageSquare, Church, GraduationCap, Loader2, Sparkles, Link2, X } from 'lucide-react';import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';

const statCards = [
  { key: 'total_members', label: 'Miembros', icon: Users, color: '#056A84', link: '/members', module: 'members' },
  { key: 'pending_members', label: 'Solicitudes', icon: Users, color: '#C58B2A', link: '/members?status=pending', module: 'members' },
  { key: 'total_events', label: 'Eventos', icon: CalendarDays, color: '#1F9D7A', link: '/events', module: 'events' },
  { key: 'upcoming_events', label: 'Próximos', icon: CalendarDays, color: '#1F9D7A', link: '/events', module: 'events' },
  { key: 'total_posts', label: 'Noticias', icon: FileText, color: '#2B7DBA', link: '/posts', module: 'posts' },
  { key: 'total_announcements', label: 'Anuncios', icon: Megaphone, color: '#056A84', link: '/announcements', module: 'announcements' },
  { key: 'total_ministries', label: 'Ministerios', icon: Church, color: '#1F9D7A', link: '/ministries', module: 'ministries' },
  { key: 'total_groups', label: 'Grupos', icon: Users, color: '#2B7DBA', link: '/groups', module: 'groups' },
  { key: 'total_courses', label: 'Cursos', icon: GraduationCap, color: '#2B7DBA', link: '/courses', module: 'courses' },
  { key: 'total_prayers', label: 'Oraciones', icon: Heart, color: '#D64545', link: '/prayers', module: 'prayers' },
  { key: 'total_contributions', label: 'Aportes pagados', icon: DollarSign, color: '#C58B2A', link: '/contributions', module: 'contributions' },
  { key: 'total_petitions', label: 'Peticiones', icon: MessageSquare, color: '#D64545', link: '/petitions', module: 'petitions' },
  { key: 'unread_petitions', label: 'Sin responder', icon: MessageSquare, color: '#D64545', link: '/petitions', module: 'petitions' },
];

const ALL_LIVE_EVENTS = [];

export default function DashboardPage() {
  const { selectedChurchId, user, canRead, isSuperAdmin } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [claims, setClaims] = useState(null);
  const [links, setLinks] = useState(null);
  const [linking, setLinking] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isSuperAdmin) { setClaims(null); return; }
    api.get('/api/subscriptions/genesis-roster/claims', { params: { days: 14 } })
      .then(res => setClaims(res.data))
      .catch(() => setClaims(null));
  }, [isSuperAdmin]);

  const loadLinks = useCallback(() => {
    if (!isSuperAdmin) { setLinks(null); return; }
    api.get('/api/subscriptions/genesis-roster/suggestions')
      .then(res => setLinks(res.data))
      .catch(() => setLinks(null));
  }, [isSuperAdmin]);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const dismissLink = async (s) => {
    setLinking(s.church_id);
    try {
      await api.post('/api/subscriptions/genesis-roster/suggestions/dismiss',
        { church_id: s.church_id, roster_id: s.roster_id });
      toast.success(`${s.church_name} ya no se sugerirá como «${s.roster_name}»`);
      loadLinks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo descartar');
    } finally { setLinking(null); }
  };

  const confirmLink = async (s) => {
    setLinking(s.church_id);
    try {
      await api.post(`/api/subscriptions/genesis-roster/${s.roster_id}/link`, { church_id: s.church_id });
      toast.success(`${s.church_name} quedó vinculada a «${s.roster_name}» con Plan Génesis`);
      loadLinks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo vincular');
    } finally { setLinking(null); }
  };

  useEffect(() => {
    setLoading(true);
    dashboardAPI.getStats(selectedChurchId)
      .then(res => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedChurchId]);

  const refreshStats = useCallback(() => {
    dashboardAPI.getStats(selectedChurchId).then(res => setStats(res.data)).catch(() => {});
  }, [selectedChurchId]);

  const live = useLiveEvents(ALL_LIVE_EVENTS, refreshStats);

  return (
    <div>
      <TopBar
        title="Dashboard"
        onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        {/* Welcome */}
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-foreground">
            Bienvenido, {user?.firstname || user?.name || 'Pastor'}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {churchInfo?.name ? `Iglesia: ${churchInfo.name}` : 'Gestión de todas las iglesias'}
          </p>
          <span
            className="inline-flex items-center gap-1.5 mt-2 text-xs text-muted-foreground"
            data-testid="dashboard-live-indicator"
          >
            <span className={`w-2 h-2 rounded-full ${live.webhookConfigured ? 'bg-[#1F9D7A] animate-pulse' : 'bg-muted-foreground/40'}`} />
            {live.webhookConfigured ? 'Datos en vivo activos' : 'Datos en vivo: webhook pendiente de registrar'}
          </span>
        </div>

        {claims?.total > 0 && (
          <button
            onClick={() => navigate('/subscriptions')}
            className="w-full text-left mb-6 p-4 rounded-xl border border-[#1F9D7A]/40 bg-[#1F9D7A]/5 hover:bg-[#1F9D7A]/10 transition-colors"
            data-testid="dashboard-roster-claims"
          >
            <p className="text-sm font-medium text-[#0B6B55] flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              {claims.total === 1
                ? '1 congregación del padrón Génesis se registró'
                : `${claims.total} congregaciones del padrón Génesis se registraron`}
            </p>
            <p className="text-xs text-[#0B6B55]/80 mt-1">
              {claims.data.map(c => c.name).join(' · ')} — ya tienen el Plan Génesis asignado.
              Quedan {claims.genesis_available} por registrarse.
            </p>
          </button>
        )}

        {links?.total > 0 && (
          <div
            className="mb-6 p-4 rounded-xl border border-[#C58B2A]/40 bg-[#C58B2A]/5"
            data-testid="dashboard-roster-suggestions"
          >
            <p className="text-sm font-medium text-[#8a6418] flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              {links.total === 1
                ? '1 congregación registrada se parece a una del padrón Génesis'
                : `${links.total} congregaciones registradas se parecen a una del padrón Génesis`}
            </p>
            <p className="text-xs text-[#8a6418]/80 mt-1">
              El reconocimiento automático no las dio por seguras. Confirma el vínculo para darles
              el Plan Génesis. Quedan {links.genesis_pending} filas del padrón por registrarse.
            </p>
            <div className="mt-3 space-y-2">
              {links.data.map(s => (
                <div
                  key={s.church_id}
                  className="flex flex-wrap items-center gap-3 bg-background/70 rounded-lg px-3 py-2"
                  data-testid={`dashboard-suggestion-${s.church_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate">
                      <span className="font-medium">{s.church_name}</span>
                      <span className="text-muted-foreground"> → {s.roster_name}</span>
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {[s.roster_pastor, s.roster_city, s.reason].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="h-8 text-xs bg-[#056A84] hover:bg-[#045A70] text-white"
                    disabled={linking === s.church_id}
                    onClick={() => confirmLink(s)}
                    data-testid={`dashboard-suggestion-link-${s.church_id}`}
                  >
                    {linking === s.church_id
                      ? <Loader2 className="w-3.5 h-3.5 spinner mr-1" />
                      : <Link2 className="w-3.5 h-3.5 mr-1" />}
                    Vincular
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={linking === s.church_id}
                    onClick={() => dismissLink(s)}
                    data-testid={`dashboard-suggestion-dismiss-${s.church_id}`}
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> No es la misma
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => navigate('/subscriptions')}
                    data-testid={`dashboard-suggestion-review-${s.church_id}`}
                  >
                    Revisar padrón
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 spinner text-[#056A84]" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {statCards
              .filter(card => canRead(card.module) && stats?.[card.key] !== null && stats?.[card.key] !== undefined)
              .map((card) => (
              <Card
                key={card.key}
                className="stat-card cursor-pointer"
                onClick={() => navigate(card.link)}
                data-testid={`dashboard-stat-${card.key}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{backgroundColor: `${card.color}15`}}>
                      <card.icon className="w-5 h-5" style={{color: card.color}} />
                    </div>
                  </div>
                  <p className="text-2xl font-semibold text-foreground" data-testid={`dashboard-stat-${card.key}-value`}>
                    {stats?.[card.key] ?? 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{card.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Amount Summary */}
        {stats?.total_ves !== null && stats?.total_ves !== undefined && (
          <Card className="mt-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Resumen financiero</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-8">
                <div>
                  <p className="text-3xl font-semibold text-[#056A84]" data-testid="dashboard-total-ves">
                    Bs. {Number(stats.total_ves || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-sm text-muted-foreground">Total recaudado</p>
                </div>
                <div>
                  <p className="text-xl font-semibold text-[#1F9D7A]" data-testid="dashboard-total-usd">
                    ${Number(stats.total_usd || 0).toFixed(2)}
                  </p>
                  <p className="text-sm text-muted-foreground">Equivalente USD</p>
                </div>
                <div>
                  <p className="text-xl font-semibold text-[#C58B2A]">{stats.total_contributions || 0}</p>
                  <p className="text-sm text-muted-foreground">Aportes registrados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
